import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Drawer, Typography, IconButton, Box, Divider, Collapse } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SpeedIcon from '@material-ui/icons/Speed';
import TuneIcon from '@material-ui/icons/Tune';
import GeofenceSection from './GeofenceSection';
import AreaTypeDefaultsModal from '../admin/AreaTypeDefaultsModal';
import PropertyLinesSection from './PropertyLinesSection';
import RestrictedAreasSection from './RestrictedAreasSection';
import RestrictedAreaDialog from '../../HMI/RestrictedAreaDialog';
import ProductionRateEditor from '../../SiteTakeoff/ProductionRateEditor';

const useStyles = makeStyles(() => ({
  drawer: {
    width: 380,
    flexShrink: 0,
  },
  paper: {
    width: 380,
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 20px',
    borderBottom: '1px solid #e0e0e0',
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: 20,
  },
  checklist: {
    padding: 14,
    borderRadius: 8,
    marginBottom: 24,
    border: '1px solid',
  },
  checklistIncomplete: {
    background: '#FFFBEB',
    borderColor: '#FCD34D',
  },
  checklistComplete: {
    background: '#ECFDF5',
    borderColor: '#A7F3D0',
  },
  checklistTitle: {
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '3px 0',
    fontSize: 12,
    color: '#555',
  },
  section: {
    marginBottom: 28,
  },
}));

/**
 * PropertySettingsDrawer — owns the setup checklist UI and renders each section.
 * Hooks and data are passed from the parent so this component stays dumb.
 */
const PropertySettingsDrawer = ({
  open,
  onClose,
  url,
  jobsite,
  setupStatus,
  // Geofence
  geofence, // { radius, saveRadius, saving }
  // Property lines
  propertyLines, // { countyLines, manualLines, usingFallback, saveManualLine, deleteManualLine, renameManualLine }
  // Restricted areas
  restrictedAreas, // { areas, saveArea, deleteArea }
  // Jobsite drawing
  startPolygonDraw,
  startPolygonEdit,
  // Production rates (per-takeoff)
  areas = [],
  rateOverrides = {},
  onUpdateRates,
}) => {
  const classes = useStyles();
  const [raDialogOpen, setRaDialogOpen] = useState(false);
  const [editingRA, setEditingRA] = useState(null);
  const [pendingRAPaths, setPendingRAPaths] = useState(null);
  const [drawingRA, setDrawingRA] = useState(false);
  const [drawingPL, setDrawingPL] = useState(false);
  const [editingPL, setEditingPL] = useState(null); // line being shape-edited
  const [ratesExpanded, setRatesExpanded] = useState(false);
  const [serviceDefaultsOpen, setServiceDefaultsOpen] = useState(false);

  const hasJobsite = !!jobsite?._id;
  const {
    areasMapped = false,
    geofenceSet = false,
    propertyLinesVerified = false,
  } = setupStatus || {};
  const allComplete = areasMapped && geofenceSet && propertyLinesVerified;

  // Restricted area handlers
  const handleDrawRA = () => {
    if (!startPolygonDraw) return;
    setDrawingRA(true);
    onClose(); // close drawer so user can draw on the map
    startPolygonDraw((paths) => {
      setDrawingRA(false);
      if (paths && paths.length >= 3) {
        setPendingRAPaths(paths);
        setEditingRA(null);
        setRaDialogOpen(true);
      }
    });
  };

  const handleEditRA = (ra) => {
    setEditingRA(ra);
    setPendingRAPaths(null);
    setRaDialogOpen(true);
  };

  const handleSaveRA = async (formData) => {
    if (editingRA?._id) {
      await restrictedAreas.saveArea(formData, editingRA);
    } else if (pendingRAPaths) {
      await restrictedAreas.saveArea({ ...formData, paths: pendingRAPaths }, null);
    }
    setRaDialogOpen(false);
    setEditingRA(null);
    setPendingRAPaths(null);
  };

  // Property line drawing
  const handleDrawPL = () => {
    if (!startPolygonDraw) return;
    setDrawingPL(true);
    onClose(); // close drawer so user can draw on the map
    startPolygonDraw(async (paths) => {
      setDrawingPL(false);
      if (paths && paths.length >= 3) {
        await propertyLines.saveManualLine(paths);
      }
    });
  };

  // Property line shape editing
  const handleEditPLShape = (line) => {
    if (!startPolygonEdit || !line?.paths) return;
    setEditingPL(line);
    onClose(); // close drawer so user can edit on the map
    startPolygonEdit(
      line.paths,
      async (updatedPaths) => {
        setEditingPL(null);
        if (updatedPaths && updatedPaths.length >= 3) {
          await propertyLines.updateManualLinePaths(line._id, updatedPaths);
        }
      },
      () => setEditingPL(null)
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        className={classes.drawer}
        classes={{ paper: classes.paper }}
        variant="temporary"
      >
        <div className={classes.header}>
          <Typography className={classes.title}>Property settings</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={classes.body}>
          {/* Setup checklist */}
          <div className={`${classes.checklist} ${allComplete ? classes.checklistComplete : classes.checklistIncomplete}`}>
            <div className={classes.checklistTitle}>
              {allComplete ? (
                <>
                  <CheckCircleIcon style={{ fontSize: 16, color: '#047857' }} />
                  <span style={{ color: '#047857' }}>All set!</span>
                </>
              ) : (
                <span style={{ color: '#92400E' }}>Setup checklist</span>
              )}
            </div>
            <div className={classes.checkItem}>
              {areasMapped
                ? <CheckCircleIcon style={{ fontSize: 16, color: '#10B981' }} />
                : <RadioButtonUncheckedIcon style={{ fontSize: 16, color: '#ccc' }} />}
              <span>Areas mapped</span>
            </div>
            <div className={classes.checkItem}>
              {geofenceSet
                ? <CheckCircleIcon style={{ fontSize: 16, color: '#10B981' }} />
                : <RadioButtonUncheckedIcon style={{ fontSize: 16, color: '#ccc' }} />}
              <span>Geofence set</span>
            </div>
            <div className={classes.checkItem}>
              {propertyLinesVerified
                ? <CheckCircleIcon style={{ fontSize: 16, color: '#10B981' }} />
                : <RadioButtonUncheckedIcon style={{ fontSize: 16, color: '#ccc' }} />}
              <span>Property lines verified</span>
            </div>
          </div>

          <Divider style={{ marginBottom: 20 }} />

          {/* Geofence */}
          <Box className={classes.section}>
            <GeofenceSection
              radius={geofence?.radius}
              onRadiusChange={() => {}}
              onRadiusCommit={(val) => geofence?.saveRadius?.(val)}
              disabled={!hasJobsite}
              repositioning={geofence?.repositioning}
              hasCustomCenter={geofence?.hasCustomCenter}
              onStartReposition={geofence?.startRepositioning}
              onConfirmReposition={geofence?.confirmRepositioning}
              onCancelReposition={geofence?.cancelRepositioning}
              onResetCenter={geofence?.resetCenter}
            />
          </Box>

          {/* Property lines */}
          <Box className={classes.section}>
            <PropertyLinesSection
              countyLines={propertyLines?.countyLines || []}
              manualLines={propertyLines?.manualLines || []}
              usingFallback={propertyLines?.usingFallback}
              countyVisible={propertyLines?.countyVisible}
              manualVisible={propertyLines?.manualVisible}
              onToggleCounty={() => propertyLines?.setCountyVisible(v => !v)}
              onToggleManual={() => propertyLines?.setManualVisible(v => !v)}
              onDrawCustom={handleDrawPL}
              onEditManualShape={handleEditPLShape}
              onDeleteManual={propertyLines?.deleteManualLine}
              onRenameManual={propertyLines?.renameManualLine}
              drawing={drawingPL}
              editing={!!editingPL}
              disabled={!hasJobsite}
            />
          </Box>

          {/* Restricted areas */}
          <Box className={classes.section}>
            <RestrictedAreasSection
              areas={restrictedAreas?.areas || []}
              onDraw={handleDrawRA}
              onEdit={handleEditRA}
              onDelete={restrictedAreas?.deleteArea}
              drawing={drawingRA}
              disabled={!hasJobsite}
            />
          </Box>

          {/* Production rates — collapsible (takeoff-scoped overrides) */}
          <Box className={classes.section}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ cursor: 'pointer', padding: '4px 0' }}
              onClick={() => setRatesExpanded(v => !v)}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <SpeedIcon style={{ fontSize: 18, color: '#666' }} />
                <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                  Production rates
                </Typography>
                {Object.keys(rateOverrides || {}).length > 0 && (
                  <span style={{
                    fontSize: 10,
                    padding: '2px 6px',
                    background: '#EEF2FF',
                    color: '#4F46E5',
                    borderRadius: 4,
                    fontWeight: 600,
                  }}>
                    Custom
                  </span>
                )}
              </Box>
              {ratesExpanded ? (
                <ExpandLessIcon style={{ fontSize: 18, color: '#999' }} />
              ) : (
                <ExpandMoreIcon style={{ fontSize: 18, color: '#999' }} />
              )}
            </Box>
            {!ratesExpanded && (
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                Click to override default rates for this takeoff
              </Typography>
            )}
            <Collapse in={ratesExpanded}>
              <Box mt={1} style={{ borderTop: '1px solid #eee', paddingTop: 8 }}>
                <ProductionRateEditor
                  areas={areas}
                  rateOverrides={rateOverrides}
                  onChange={onUpdateRates}
                />
              </Box>
            </Collapse>
          </Box>

          <Divider style={{ margin: '8px 0' }} />

          {/* Service Defaults (Admin) */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ padding: '4px 0', cursor: 'pointer' }}
            onClick={() => setServiceDefaultsOpen(true)}
          >
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <TuneIcon style={{ fontSize: 18, color: '#666' }} />
              <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                Service defaults
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              Configure
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
            Set which services auto-assign per area type
          </Typography>
        </div>
      </Drawer>

      {/* Area Type Service Defaults Modal */}
      <AreaTypeDefaultsModal
        open={serviceDefaultsOpen}
        onClose={() => setServiceDefaultsOpen(false)}
        url={url}
      />

      {/* Restricted Area Dialog — reuses existing HMI dialog */}
      <RestrictedAreaDialog
        open={raDialogOpen}
        onClose={() => {
          setRaDialogOpen(false);
          setEditingRA(null);
          setPendingRAPaths(null);
        }}
        onSave={handleSaveRA}
        editingArea={editingRA}
        url={url}
        clientId={jobsite?.client}
      />
    </>
  );
};

export default PropertySettingsDrawer;
